import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { loadRawEntityDataByIDOrFail } from '@adornis/bh-util/util.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import type { Message } from '@adornis/mails/server/communication.js';
import { differenceInDays, format } from 'date-fns';
import { readFile } from 'fs/promises';
import type { TemplateResult } from 'lit';
import { getMailTemplateForInstitution } from '../api/mail-templates.js';
import { getWorkshopAppointmentParticipationConfirmationLink } from '../api/participation.js';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { Institution } from '../entities/institution.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { getMergedProductSettings, SettingColorDefintionProp } from '../entities/settings.js';
import { Teaching } from '../entities/teaching.js';
import { User } from '../entities/user.js';
import { Workshop } from '../entities/workshop.js';
import { assembleInstitutionMail } from '../mails/assemble-mail.js';
import { institutionMailHint, institutionMailVariables } from '../mails/mail-template-catalog.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { generateEvaluationQrCodePdf, generateTnQrCodePdf } from './qr-pdf-generation.js';
import { getAdornisFileByID } from '../api/files.js';

defineMailTemplate(MailTemplateType.LECTURER_WORKSHOP_REMINDER, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Informationen zu Ihrer bevorstehenden Veranstaltung',
    content: `<p>Sehr geehrte/r {{title}} {{firstname}} {{lastname}},</p>
<p>Ihre Veranstaltung "<strong>{{product-name}}</strong>" findet in Kürze statt. Nachfolgend finden Sie die wichtigsten Informationen:</p>
<h3>Veranstaltungsdetails:</h3>
<p>{{termin-card}}</p>
<h3>Allgemeine Hinweise:</h3>
<p>{{event-location}}</p>
<p><strong>Anfahrt und Parkplatz:</strong> Bitte planen Sie ausreichend Zeit für die Anreise ein. Informationen zu Parkmöglichkeiten finden Sie auf unserer Website.</p>
<p>Bei Fragen oder Problemen stehen wir Ihnen gerne zur Verfügung.</p>
<p>Vielen Dank für Ihr Engagement!</p>
<p>Mit freundlichen Grüßen</p>`,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.LECTURER_WORKSHOP_REMINDER),
  attachments: [],
  displayName: 'Dozenten 1. Erinnerungsmail',
  editorHint: () => institutionMailHint(MailTemplateType.LECTURER_WORKSHOP_REMINDER),
});

export async function notifyLecturerWorkshopReminder(params: { teachingID: string; workshopID: string }) {
  const { teachingID, workshopID } = params;

  // Load teaching data
  const teaching = await loadRawEntityDataByIDOrFail(Teaching, teachingID, {
    _id: 1,
    _class: 1,
    lecturerID: 1,
    productID: 1,
  });

  // Load workshop data
  const workshop = await loadRawEntityDataByIDOrFail(Workshop, workshopID, {
    _class: 1,
    _id: 1,
    name: 1,
    description: 1,
    institutionID: 1,
    appointments: { _id: 1, start: 1, end: 1, location: 1, name: 1 },
  });

  // Load instructor (lecturer) data
  const lecturer = await loadRawEntityDataByIDOrFail(User, teaching.lecturerID, {
    _id: 1,
    _class: 1,
    email: 1,
    firstName: 1,
    lastName: 1,
    title: 1,
    whatsappPhone: 1,
    allowWhatsapp: 1,
    whatsappNotification: 1,
  });

  // Load institution data
  const institution = await loadRawEntityDataByIDOrFail(Institution, workshop.institutionID, {
    _id: 1,
    name: 1,
    logoID: 1,
    colorDefinitions: selectionSet(() => ColorDefinitions, 4),
    settings: {
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
    },
  });

  // Load product settings
  const productSettings = await getMergedProductSettings(workshop._id, [
    'courseLocation',
    'evaluationLink',
    'evaluationLinkCode',
  ]);

  // Set mail theme
  bhBaseQlContext.colorDefinitions =
    institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;

  // Get mail template
  const mailTemplate = await getMailTemplateForInstitution(
    workshop.institutionID,
    MailTemplateType.LECTURER_WORKSHOP_REMINDER,
  )(InstitutionMailTemplate.allFields);

  // Calculate days till start
  const firstAppointmentStart = workshop.appointments?.find(a => a.start)?.start;
  const daysTillStart = firstAppointmentStart ? differenceInDays(firstAppointmentStart, new Date()) : null;

  // Prepare mail variables
  const dates = workshop.appointments
    ?.map(a => a.start)
    .filter((d): d is Date => d !== undefined && d !== null)
    .map(d => format(d, 'dd.MM.yyyy'))
    .join(', ');

  // Build evaluation link with code
  const evaluationLink = productSettings.evaluationLink ?? '';
  const evaluationCode = productSettings.evaluationLinkCode ?? '';
  const separator = evaluationLink.endsWith('?') ? '' : '?';
  const fullEvaluationLink = evaluationCode ? `${evaluationLink}${separator}p=${evaluationCode}` : evaluationLink;

  const mailVars = {
    [MailTemplateVars.TITLE]: lecturer.title ?? '',
    [MailTemplateVars.FIRST_NAME]: InstitutionMailTemplate.mapFirstNameVar(lecturer) ?? '',
    [MailTemplateVars.LAST_NAME]: InstitutionMailTemplate.mapLastNameVar(lecturer) ?? '',
    [MailTemplateVars.PRODUCT_NAME]: workshop.name ?? '',
    [MailTemplateVars.PRODUCT_CARD]: InstitutionMailTemplate.mapProductVar(workshop) ?? '',
    [MailTemplateVars.EVENT_LOCATION]: productSettings?.courseLocation ?? '',
    [MailTemplateVars.EVENT_DATES]: dates ?? '',
    [MailTemplateVars.EVENT_LEAD_DAYS]: daysTillStart?.toString() ?? '',
    [MailTemplateVars.EVALUATION_LINK]: fullEvaluationLink,
    [MailTemplateVars.EVALUATION_CODE]: evaluationCode,
  };

  const { subject, content } = await assembleInstitutionMail({ template: mailTemplate, infos: mailVars as any });

  if (!content) throw new Error('mail template has no content!');
  if (!subject) throw new Error('mail template has no subject!');

  // Load institution logo if available
  let logoBuffer: Buffer | null = null;
  if (institution.logoID) {
    try {
      const logoFile = await getAdornisFileByID(institution.logoID)(selectionSet(() => AdornisFile, 2));
      if (logoFile) {
        logoBuffer = await readFile(logoFile.getFullPath());
      }
    } catch {
      // Logo loading failed, continue without logo
    }
  }

  // Get confirmation link for the triggered appointment
  const allConfirmationLinks = await getWorkshopAppointmentParticipationConfirmationLink(workshop._id)({
    appointmentID: 1,
    url: 1,
    validFrom: 1,
    validTill: 1,
  });

  const attachments = [] as NonNullable<Message['attachments']>;

  // TN (attendance confirmation) QR code attachments for all appointments
  if (allConfirmationLinks.length > 0) {
    const tnPdf = await generateTnQrCodePdf({
      workshopName: workshop.name ?? '',
      courseLocation: productSettings?.courseLocation ?? '',
      appointments: workshop.appointments ?? [],
      confirmationLinks: allConfirmationLinks,
      logoBuffer,
    });
    attachments.push({ filename: 'teilnahmebestaetigung-qr.pdf', content: tnPdf, contentType: 'application/pdf' });
  }

  // Evaluation QR code attachments
  if (evaluationLink) {
    const evalPdf = await generateEvaluationQrCodePdf({
      workshopName: workshop.name ?? '',
      courseLocation: productSettings?.courseLocation ?? '',
      appointments: workshop.appointments ?? [],
      evaluationLink,
      evaluationCode,
      logoBuffer,
    });
    attachments.push({ filename: 'evaluation-qr.pdf', content: evalPdf, contentType: 'application/pdf' });
  }

  await sendInstitutionEmail({
    institutionID: institution._id,
    email: lecturer.email,
    subject,
    content: content as TemplateResult<1>,
    attachments,
  });
}
